import * as React from "react";
import { Slot } from "radix-ui";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  cn(
    "inline-flex items-center justify-center gap-2 rounded-4 text-[12.5px] font-medium",
    "transition-colors disabled:pointer-events-none disabled:opacity-50",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-p-600 focus-visible:ring-offset-2 focus-visible:ring-offset-surface",
  ),
  {
    variants: {
      variant: {
        primary: cn(
          "bg-p-600 text-white",
          "shadow-[inset_0_1px_0_var(--btn-primary-highlight),0_1px_0_var(--btn-primary-edge)]",
          "hover:bg-p-500 active:bg-p-700",
        ),
        default: cn(
          "bg-surface text-ink-700 border border-line-strong",
          "hover:bg-surface-1 hover:border-ink-300",
        ),
        ghost: "bg-transparent text-ink-700 hover:bg-surface-1",
        "danger-soft": cn(
          "bg-vroom-red-soft text-vroom-red border border-vroom-red-edge",
          "hover:bg-vroom-red-soft/80",
        ),
      },
      size: {
        compact: "h-7 px-2.5",
        default: "h-8 px-3",
        touch: "h-11 px-4",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export interface ButtonProps
  extends React.ComponentProps<"button">, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const classes = cn(buttonVariants({ variant, size }), className);
  if (asChild) {
    return (
      <Slot.Root
        data-slot="button"
        data-loading={loading || undefined}
        className={classes}
        aria-busy={loading || undefined}
        aria-disabled={disabled || undefined}
        {...props}
      >
        {children}
      </Slot.Root>
    );
  }
  return (
    <button
      data-slot="button"
      data-loading={loading || undefined}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? (
        <Loader2 aria-hidden className="size-3.5 animate-spin" data-slot="button-spinner" />
      ) : null}
      {children}
    </button>
  );
}

export { Button, buttonVariants };
